import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getFaultReports } from "../../../store/slices/fault-module/fault-report.slice";
import FaultReportTableComponent from "./fault-report-table.component";
import TableSort from "../../../shared/helpers/table-sort";
import capitalizeFirst from "../../../shared/helpers/upper-case";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye } from "@fortawesome/free-solid-svg-icons";

const FaultReportComponent = () => {
  const [PageNumber, setPageNum] = useState<number>(1);
  const pageSize = 10;
  const navigate = useNavigate();

  const filter = useSelector((state: any) => {
    return state.faultReport.filter;
  });

  const [SortBy, setSortBy] = useState<any>(capitalizeFirst("timestamp"));
  const [SortDirection, setSortDirection] = useState<any>("desc");

  const getFloorplanViewPath = (row: any) => {
    const blockCode = row?.blockCode;
    const blockToFloorCode = row?.blockToFloorCode;
    const assetCode = row?.assetCode;

    if (!blockCode || !blockToFloorCode || !assetCode) {
      return;
    }

    const floorplanPath = `/dashboard/${btoa(
      blockCode,
    )}/floorplan/${encodeURIComponent(blockToFloorCode)}?assetCode=${btoa(
      assetCode,
    )}`;

    return floorplanPath;
  };

  const renderPropForFloorPlanView = (row: any) => {
    const floorplanPath = getFloorplanViewPath(row);
    const isDisabled = !floorplanPath;

    return (
      <button
        type="button"
        className="d-flex align-items-center gap-1"
        title={isDisabled ? "Asset Not Annotated" : "View in Floor Plan"}
        style={{
          cursor: isDisabled ? "not-allowed" : "pointer",
          opacity: isDisabled ? 0.5 : 1,
          color: isDisabled ? "#999" : "inherit",
          background: "none",
          border: "none",
          padding: 0,
        }}
        onClick={() => {
          if (!isDisabled) {
            navigate(floorplanPath);
          }
        }}
      >
        <FontAwesomeIcon icon={faEye} className="fs-7 me-2" />
      </button>
    );
  };

  const [columns, setColumns] = useState([
    {
      title: "Location",
      label: "address",
      type: "link",
      link: "/dashboard",
      linkableField: "blockCode",
      width: "15rem",
    },
    {
      title: "Asset ID",
      label: "assetCode",
      width: "18rem",
      type: "link",
      link: "/asset",
      linkableField: "assetCode",
    },
    {
      title: "Display Name",
      label: "sensorDisplayName",
      width: "13rem",
    },
    {
      title: "Floor Plan",
      label: "floorplanPath",
      renderProps: renderPropForFloorPlanView,
      width: "7rem",
      excludeSort: true,
    },
    {
      title: "Status",
      label: "status",
    },
    {
      title: "Report Date & Time",
      label: "timestamp",
      type: "datetime",
      width: "18rem",
      isSorted: true,
    },
    {
      title: "Fault Type",
      label: "faultType",
      width: "13rem",
    },
    {
      title: "Fault Description",
      label: "faultDescription",
      width: "13rem",
    },
    {
      title: "Acknowledged By",
      label: "acknowledgeBy",
      width: "13rem",
    },
    {
      title: "Acknowledged Time",
      label: "acknowledgeDate",
      type: "datetime",
      width: "15rem",
    },
    {
      title: "Resolved By",
      label: "resolvedBy",
      width: "13rem",
    },
    {
      title: "Resolved Time",
      label: "resolveDate",
      type: "datetime",
      width: "13rem",
    },
    {
      title: "Remarks",
      label: "remarks",
      // width: "12rem",
    },
  ]);

  const dispatch = useDispatch();

  const faultReportData = useSelector((state: any) => {
    return state.faultReport.faultReports;
  });

  const isLoadingFaultReports = useSelector((state: any) => {
    return state.faultReport.isLoadingFaultReports;
  });

  const onRefetchData = (
    pageNumber = 1,
    tcCode = null,
    siteCode = null,
    blockCode = null,
    reportType = null,
    startDate = null,
    endDate = null,
    isAcknowledged = 3
  ) => {
    dispatch(
      getFaultReports({
        PageNumber: pageNumber,
        pageSize,
        tcCode,
        siteCode,
        blockCode,
        reportType,
        startDate,
        endDate,
        SortBy,
        SortDirection,
        isAcknowledged,
      })
    );
  };

  const handleChangePage = useCallback((page: number): void => {
    setPageNum(page);
  }, []);

  const handleChangeSort = useCallback(
    async (sortBy: string, sortDirection: any) => {
      if (!sortBy || !sortDirection) return;
      setSortBy(sortBy);
      setSortDirection(sortDirection);

      const copyColumns: any = await TableSort(columns, sortBy);
      setColumns(copyColumns);
    },
    []
  );

  useEffect(() => {
    let {
      townCouncil,
      site,
      block,
      reportType,
      startDate,
      endDate,
      isAcknowledged,
    } = filter;

    if (townCouncil && reportType && startDate) {
      let reportTypeStatus =
        (reportType === "null" || reportType === "all") ? null : reportType;

      dispatch(
        getFaultReports({
          PageNumber: PageNumber,
          pageSize,
          tcCode: townCouncil,
          siteCode: site || null,
          blockCode: block || null,
          reportType: reportTypeStatus || "null",
          startDate: startDate,
          endDate: endDate || null,
          SortBy,
          SortDirection,
          isAcknowledged,
        })
      );
    }
  }, [dispatch, PageNumber, SortBy, SortDirection]);

  useEffect(() => {
    setPageNum(1);
  }, [filter]);

  return (
    <div>
      <FaultReportTableComponent
        columns={columns}
        data={faultReportData?.data}
        totalNumberOfRecords={faultReportData?.numberOfRecords}
        pageSize={pageSize}
        current={PageNumber}
        onChangePage={handleChangePage}
        onRefetchData={onRefetchData}
        onChangeSort={handleChangeSort}
        isLoading={isLoadingFaultReports}
      />
    </div>
  );
};

export default FaultReportComponent;
